import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Navbar } from "@/components/Navbar";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, TrendingUp, Target, CheckCircle2, X, Flame, Star, Award, Lock, Mail, Bell, ArrowLeftRight, Crown, Gem, BookOpen } from "lucide-react";

function computeBadges(completed, goals) {
  const best = completed.reduce((m, g) => Math.max(m, g.health_score || 0), 0);
  const totalCheckins = goals.reduce((s, g) => s + (g.checked_in_count || 0), 0);
  const improved = completed.some((g, i) => {
    const nxt = completed[i + 1];
    return nxt && (g.health_score || 0) > (nxt.health_score || 0);
  });
  return [
    { id: "first", label: "First Look", desc: "Created your first reveal", icon: Sparkles, tier: "common", earned: completed.length >= 1 },
    { id: "goalsetter", label: "Goal Setter", desc: "Committed to a glow goal", icon: Target, tier: "common", earned: goals.length >= 1 },
    { id: "streak3", label: "On a Roll", desc: "3 goal check-ins", icon: Flame, tier: "common", earned: totalCheckins >= 3 },
    { id: "glowup", label: "Glow Getter", desc: "Improved your score", icon: TrendingUp, tier: "common", earned: improved },
    { id: "streak7", label: "Streak Master", desc: "7 goal check-ins", icon: Award, tier: "rare", earned: totalCheckins >= 7 },
    { id: "radiant", label: "Radiant", desc: "Hit a 75+ score", icon: Star, tier: "rare", earned: best >= 75 },
    { id: "legend", label: "Legend", desc: "30 goal check-ins", icon: Crown, tier: "legendary", earned: totalCheckins >= 30 },
    { id: "flawless", label: "Flawless", desc: "Reached a 90+ score", icon: Gem, tier: "legendary", earned: best >= 90 },
  ];
}

const TIER_RANK = { common: 0, rare: 1, legendary: 2 };

function fireConfetti(tier) {
  const end = Date.now() + 800;
  const colors =
    tier === "legendary"
      ? ["#C9A227", "#8E5CF7", "#F4D35E"]
      : ["#E88D67", "#8E9E82", "#F4C9B4"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.35 }, colors });
}

function playChime(tier) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = tier === "legendary"
      ? [523.25, 659.25, 783.99, 987.77, 1318.51] // richer legendary fanfare
      : [523.25, 659.25, 783.99, 1046.5]; // C E G C
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      const t = ctx.currentTime + i * 0.11;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.18, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.55);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch (_) {}
}

export default function History() {
  const [gens, setGens] = useState(null);
  const [goals, setGoals] = useState([]);
  const [goalsLoaded, setGoalsLoaded] = useState(false);
  const [shine, setShine] = useState("");
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const celebratedRef = useRef(false);
  const { user, updateUser } = useAuth();
  const [emailInput, setEmailInput] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [recap, setRecap] = useState(null);

  const openRecapPreview = async () => {
    setRecapOpen(true);
    setRecap(null);
    try {
      const { data } = await api.get("/recap/preview");
      setRecap(data);
    } catch (e) {
      toast.error(apiError(e));
      setRecapOpen(false);
    }
  };

  const sendRecap = async () => {
    setSendingRecap(true);
    try {
      const { data } = await api.post("/recap/send-me");
      toast.success(`Your glow-up story is on its way to ${data.email}! 🌟`);
      setRecapOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSendingRecap(false);
    }
  };

  const saveReminders = async (value, cadence) => {
    setSavingEmail(true);
    try {
      const payload = { email: value || null };
      if (cadence) payload.cadence = cadence;
      const { data } = await api.put("/auth/email", payload);
      updateUser({ email: data.email, cadence: data.cadence });
      if (value && !cadence) toast.success("Reminders on! We'll email you. 📬");
      else if (cadence) toast.success(`Reminder cadence set to ${cadence}.`);
      else toast.success("Reminders turned off.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingEmail(false);
    }
  };

  const loadGoals = () => api.get("/goals").then((r) => setGoals(r.data)).catch(() => {}).finally(() => setGoalsLoaded(true));

  useEffect(() => {
    api.get("/generations").then((res) => setGens(res.data)).catch(() => setGens([]));
    loadGoals();
  }, []);

  const completed = (gens || []).filter((g) => g.status === "complete");

  useEffect(() => {
    if (gens === null || !goalsLoaded || celebratedRef.current) return;
    celebratedRef.current = true;
    const badges = computeBadges(completed, goals);
    const earnedIds = badges.filter((b) => b.earned).map((b) => b.id);
    if (earnedIds.length === 0) return;
    api.post("/badges/mark-seen", { earned: earnedIds }).then(({ data }) => {
      const fresh = badges.filter((b) => data.new?.includes(b.id));
      if (fresh.length) {
        const topTier = fresh.reduce(
          (t, b) => (TIER_RANK[b.tier] > TIER_RANK[t] ? b.tier : t),
          "common"
        );
        fireConfetti(topTier);
        if (topTier !== "common") {
          playChime(topTier);
          setShine(topTier);
          setTimeout(() => setShine(""), topTier === "legendary" ? 2200 : 1600);
        }
        fresh.forEach((b, i) =>
          setTimeout(() => {
            const prefix = b.tier === "legendary" ? "LEGENDARY badge" : b.tier === "rare" ? "Rare badge" : "Badge";
            toast.success(`${prefix} unlocked: ${b.label}! 🎉`);
          }, i * 700)
        );
      }
    }).catch(() => {});
  }, [gens, goalsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = [...completed]
    .reverse()
    .map((g, i) => ({
      name: `#${i + 1}`,
      score: g.health_score,
      date: new Date(g.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    }));

  const best = completed.reduce((m, g) => Math.max(m, g.health_score || 0), 0);
  const latest = completed[0]?.health_score ?? null;
  const prev = completed[1]?.health_score ?? null;
  const delta = latest != null && prev != null ? latest - prev : null;

  const activeGoals = goals.filter((g) => g.status === "active");

  const BADGES = computeBadges(completed, goals);
  const earnedCount = BADGES.filter((b) => b.earned).length;

  const checkIn = async (id) => {
    try {
      await api.post(`/goals/${id}/checkin`);
      toast.success("Nice work — keep it up! 🔥");
      loadGoals();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const archive = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      loadGoals();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      {shine && <div className={"shine-overlay" + (shine === "legendary" ? " shine-legendary" : "")} data-testid="shine-overlay" />}
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Your journey</span>
            <h1 className="font-heading text-4xl text-charcoal mt-2">Glow-Up History</h1>
          </div>
          <Link to="/create" className="btn-primary px-6 py-3 font-semibold inline-flex items-center gap-2">
            New <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {gens !== null && completed.length > 0 && (
          <div className="flex justify-end -mt-4 mb-6">
            <button
              data-testid="preview-recap-btn"
              onClick={openRecapPreview}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 border border-black/10 hover:bg-black/5 transition-colors"
            >
              <BookOpen className="w-4 h-4" style={{ color: "var(--primary)" }} /> My glow-up story
            </button>
          </div>
        )}

        {gens === null ? (
          <p className="text-muted-c">Loading…</p>
        ) : completed.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 soft-shadow border border-black/5 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--primary)" }} />
            <h3 className="font-heading text-2xl text-charcoal mb-2">No reveals yet</h3>
            <p className="text-muted-c mb-6">Create your first future-self portrait to see it here.</p>
            <Link to="/create" className="btn-primary inline-block px-8 py-3.5 font-semibold">Start now</Link>
          </div>
        ) : (
          <>
            {/* Habit Streaks */}
            <div className="grid lg:grid-cols-12 gap-6 mb-12" data-testid="streak-section">
              <div className="lg:col-span-8 bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-black/5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5" style={{ color: "var(--primary)" }} />
                  <h2 className="font-heading text-xl text-charcoal">Health score over time</h2>
                </div>
                {chartData.length < 2 ? (
                  <p className="text-sm text-muted-c py-10 text-center">
                    Retake the quiz after building better habits to watch your score climb here.
                  </p>
                ) : (
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,26,26,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#666" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#666" }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", fontFamily: "Manrope" }}
                          formatter={(v) => [`${v}/100`, "Score"]}
                        />
                        <Line type="monotone" dataKey="score" stroke="#E88D67" strokeWidth={3} dot={{ r: 5, fill: "#E88D67" }} activeDot={{ r: 7 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-6">
                <Stat label="Latest score" value={latest != null ? `${latest}` : "—"} sub={delta != null ? `${delta >= 0 ? "+" : ""}${delta} vs last` : "Your newest reveal"} positive={delta == null || delta >= 0} />
                <Stat label="Personal best" value={`${best}`} sub={`${completed.length} reveal${completed.length > 1 ? "s" : ""} total`} positive />
              </div>
            </div>

            {/* Streak Badges */}
            <div className="mb-12" data-testid="badges-section">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-5 h-5" style={{ color: "var(--primary)" }} />
                <h2 className="font-heading text-2xl text-charcoal">Badges</h2>
              </div>
              <p className="text-sm text-muted-c mb-5">{earnedCount} of {BADGES.length} earned — keep checking in to unlock more.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {BADGES.map((b) => {
                  const accent = b.tier === "legendary" ? "#C9A227" : b.tier === "rare" ? "#8E5CF7" : "#E88D67";
                  return (
                    <div
                      key={b.id}
                      data-testid={`badge-${b.id}`}
                      data-tier={b.tier}
                      className="rounded-3xl p-5 border-2 text-center transition-transform hover:scale-[1.03] relative overflow-hidden"
                      style={{
                        background: b.earned
                          ? (b.tier === "legendary" ? "linear-gradient(150deg,#FFFDF5,#FBF3D9)" : "#fff")
                          : "rgba(26,26,26,0.02)",
                        borderColor: b.earned ? accent + "66" : "rgba(26,26,26,0.06)",
                        boxShadow: b.earned ? `0 8px 30px -12px ${accent}88` : "none",
                        opacity: b.earned ? 1 : 0.55,
                      }}
                    >
                      {b.earned && b.tier !== "common" && (
                        <span
                          className="absolute top-2.5 right-2.5 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full"
                          style={{ background: accent + "1f", color: accent }}
                        >
                          {b.tier}
                        </span>
                      )}
                      <div
                        className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-2"
                        style={{ background: b.earned ? accent + "1f" : "rgba(26,26,26,0.05)" }}
                      >
                        {b.earned ? (
                          <b.icon className="w-5 h-5" style={{ color: accent }} />
                        ) : (
                          <Lock className="w-4 h-4 text-muted-c" />
                        )}
                      </div>
                      <div className="font-semibold text-sm text-charcoal leading-tight">{b.label}</div>
                      <div className="text-[11px] text-muted-c mt-1 leading-snug">{b.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legendary Wall */}
            {(() => {
              const legendary = BADGES.filter((b) => b.tier === "legendary");
              const earnedLegendary = legendary.filter((b) => b.earned);
              return (
                <div className="mb-12" data-testid="legendary-wall">
                  <div
                    className="rounded-3xl p-6 sm:p-8 border-2 relative overflow-hidden"
                    style={{
                      background: "linear-gradient(150deg,#1A1730 0%,#2A2140 55%,#3A2A1A 100%)",
                      borderColor: "rgba(201,162,39,0.5)",
                      boxShadow: "0 20px 60px -24px rgba(201,162,39,0.5)",
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Crown className="w-5 h-5" style={{ color: "#F4D35E" }} />
                      <h2 className="font-heading text-2xl" style={{ color: "#F4D35E" }}>Legendary Wall</h2>
                    </div>
                    <p className="text-sm mb-6" style={{ color: "rgba(255,255,255,0.6)" }}>
                      {earnedLegendary.length > 0
                        ? "The rarest honors — reserved for the truly dedicated."
                        : "Empty for now. Unlock these elite badges to light up your wall."}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 sm:gap-6 max-w-xl">
                      {legendary.map((b) => (
                        <div
                          key={b.id}
                          data-testid={`legendary-${b.id}`}
                          data-earned={b.earned}
                          className="rounded-3xl p-6 text-center relative transition-transform hover:scale-[1.03]"
                          style={{
                            background: b.earned ? "linear-gradient(150deg,#FFF7DA,#F1D98C)" : "rgba(255,255,255,0.05)",
                            border: b.earned ? "2px solid #C9A227" : "2px dashed rgba(255,255,255,0.15)",
                            boxShadow: b.earned ? "0 10px 40px -12px rgba(244,211,94,0.7)" : "none",
                          }}
                        >
                          <div
                            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3"
                            style={{ background: b.earned ? "rgba(201,162,39,0.2)" : "rgba(255,255,255,0.06)" }}
                          >
                            {b.earned ? (
                              <b.icon className="w-7 h-7" style={{ color: "#8a6d13" }} />
                            ) : (
                              <Lock className="w-5 h-5" style={{ color: "rgba(255,255,255,0.35)" }} />
                            )}
                          </div>
                          <div className="font-heading text-lg" style={{ color: b.earned ? "#3a2e08" : "rgba(255,255,255,0.75)" }}>{b.label}</div>
                          <div className="text-xs mt-1" style={{ color: b.earned ? "#7a6416" : "rgba(255,255,255,0.4)" }}>{b.desc}</div>
                          {b.earned && (
                            <span className="absolute top-3 right-3 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full" style={{ background: "#C9A227", color: "#1A1730" }}>
                              Unlocked
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

              <div className="mb-12" data-testid="goals-section">
                <div className="flex items-center gap-2 mb-5">
                  <Target className="w-5 h-5" style={{ color: "var(--sage)" }} />
                  <h2 className="font-heading text-2xl text-charcoal">Your glow goals</h2>
                </div>

                <div className="bg-white rounded-3xl p-6 soft-shadow border border-black/5 mb-4" data-testid="reminders-panel">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="w-4 h-4" style={{ color: "var(--primary)" }} />
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Reminders</span>
                  </div>
                  {user?.email ? (
                    <div className="mt-2">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-charcoal">
                          We'll email <span className="font-semibold">{user.email}</span> {user.cadence || "weekly"} when a check-in is due.
                        </p>
                        <button
                          data-testid="reminders-off-btn"
                          onClick={() => saveReminders("")}
                          disabled={savingEmail}
                          className="text-sm font-semibold text-muted-c hover:text-charcoal transition-colors"
                        >
                          Turn off
                        </button>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs text-muted-c mb-1.5">How often?</div>
                        <div className="flex gap-2" data-testid="cadence-selector">
                          {["daily", "weekly", "monthly"].map((c) => {
                            const active = (user.cadence || "weekly") === c;
                            return (
                              <button
                                key={c}
                                data-testid={`cadence-${c}`}
                                onClick={() => !active && saveReminders(user.email, c)}
                                disabled={savingEmail}
                                className="px-4 py-2 rounded-full border-2 font-semibold text-xs capitalize transition-colors"
                                style={{
                                  borderColor: active ? "var(--primary)" : "rgba(26,26,26,0.08)",
                                  background: active ? "rgba(232,141,103,0.08)" : "#fff",
                                  color: "var(--charcoal)",
                                }}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        data-testid="send-recap-btn"
                        onClick={openRecapPreview}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 border border-black/10 hover:bg-black/5 transition-colors"
                      >
                        <BookOpen className="w-4 h-4" style={{ color: "var(--primary)" }} />
                        Preview my glow-up story
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <p className="text-sm text-muted-c w-full sm:w-auto sm:flex-1">
                        Add your email to get a friendly nudge to check in on your goals.
                      </p>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <div className="relative flex-1">
                          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-c" />
                          <input
                            data-testid="reminders-email-input"
                            type="email"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            placeholder="you@email.com"
                            className="w-full rounded-full border border-black/10 pl-9 pr-4 py-2.5 bg-[#FAF8F5] outline-none focus:border-[var(--primary)] transition-colors text-sm"
                          />
                        </div>
                        <button
                          data-testid="reminders-save-btn"
                          onClick={() => emailInput && saveReminders(emailInput)}
                          disabled={savingEmail || !emailInput}
                          className="btn-primary px-5 py-2.5 text-sm font-semibold whitespace-nowrap"
                        >
                          {savingEmail ? "Saving…" : "Enable"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {activeGoals.map((g) => (
                    <div key={g.id} data-testid={`goal-${g.id}`} className="bg-white rounded-3xl p-6 soft-shadow border border-black/5 relative">
                      <button onClick={() => archive(g.id)} data-testid={`goal-archive-${g.id}`} className="absolute top-4 right-4 text-muted-c hover:text-charcoal transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                      <p className="text-sm text-charcoal leading-relaxed pr-6">{g.tip}</p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xs text-muted-c">
                          {g.checked_in_count > 0 ? `${g.checked_in_count} check-in${g.checked_in_count > 1 ? "s" : ""} · ` : ""}
                          Check back by {new Date(g.check_in_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                        <button onClick={() => checkIn(g.id)} data-testid={`goal-checkin-${g.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--sage)" }}>
                          <CheckCircle2 className="w-4 h-4" /> I did it
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compare Reveals */}
            {completed.length >= 2 && (
              <div className="mb-12" data-testid="compare-section">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowLeftRight className="w-5 h-5" style={{ color: "var(--primary)" }} />
                  <h2 className="font-heading text-2xl text-charcoal">Compare glow-ups</h2>
                </div>
                <p className="text-sm text-muted-c mb-5">Put two reveals side by side to see how far you've come.</p>
                <div className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-black/5">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <select
                      data-testid="compare-select-a"
                      value={compareA}
                      onChange={(e) => setCompareA(e.target.value)}
                      className="rounded-2xl border border-black/10 px-4 py-2.5 bg-[#FAF8F5] text-sm outline-none focus:border-[var(--primary)]"
                    >
                      <option value="" key="__empty">Select reveal…</option>
                      {completed.map((g) => (
                        <option key={g.id} value={g.id}>
                          {new Date(g.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · score {g.health_score}
                        </option>
                      ))}
                    </select>
                    <select
                      data-testid="compare-select-b"
                      value={compareB}
                      onChange={(e) => setCompareB(e.target.value)}
                      className="rounded-2xl border border-black/10 px-4 py-2.5 bg-[#FAF8F5] text-sm outline-none focus:border-[var(--primary)]"
                    >
                      <option value="" key="__empty">Select reveal…</option>
                      {completed.map((g) => (
                        <option key={g.id} value={g.id}>
                          {new Date(g.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · score {g.health_score}
                        </option>
                      ))}
                    </select>
                  </div>
                  {(() => {
                    const a = completed.find((g) => g.id === compareA);
                    const b = completed.find((g) => g.id === compareB);
                    if (!a || !b || a.id === b.id) {
                      return <p className="text-sm text-muted-c text-center py-6">Pick two different reveals above to compare.</p>;
                    }
                    const diff = (b.health_score || 0) - (a.health_score || 0);
                    return (
                      <div data-testid="compare-view">
                        <div className="grid grid-cols-2 gap-4 sm:gap-6">
                          {[a, b].map((g, idx) => (
                            <div key={g.id}>
                              <div className="rounded-3xl overflow-hidden border border-black/5 soft-shadow aspect-[4/5] relative">
                                <img src={fileUrl(g.aged_path)} alt="reveal" className="w-full h-full object-cover" />
                                <div className="absolute bottom-3 left-3 glass rounded-2xl px-3 py-2 border border-white/40">
                                  <div className="font-heading text-2xl text-charcoal leading-none">{g.health_score}</div>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-muted-c">
                                {idx === 0 ? "A · " : "B · "}
                                {new Date(g.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-5 text-center">
                          <span
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-sm"
                            style={{
                              background: diff >= 0 ? "rgba(142,158,130,0.12)" : "rgba(197,107,74,0.12)",
                              color: diff >= 0 ? "var(--sage)" : "#C56B4A",
                            }}
                          >
                            <TrendingUp className="w-4 h-4" style={{ transform: diff < 0 ? "scaleY(-1)" : "none" }} />
                            {diff === 0 ? "Same score" : `${diff > 0 ? "+" : ""}${diff} points ${diff > 0 ? "healthier" : "lower"} (A → B)`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Reveals grid */}
            <h2 className="font-heading text-2xl text-charcoal mb-5">All reveals</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="history-grid">
              {completed.map((g, i) => (
                <motion.div key={g.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <Link to={`/reveal/${g.id}`} data-testid={`history-item-${g.id}`} className="block group">
                    <div className="rounded-3xl overflow-hidden border border-black/5 soft-shadow aspect-[4/5] relative">
                      <img src={fileUrl(g.aged_path)} alt="aged" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute bottom-3 left-3 glass rounded-2xl px-3 py-2 border border-white/40">
                        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-sage">Score</div>
                        <div className="font-heading text-2xl text-charcoal leading-none">{g.health_score}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-muted-c">
                      {new Date(g.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={recapOpen} onOpenChange={setRecapOpen}>
        <DialogContent className="max-w-lg" data-testid="recap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Your glow-up story 🌿</DialogTitle>
          </DialogHeader>
          {!recap ? (
            <p className="text-sm text-muted-c py-8 text-center">Gathering your story…</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Reveals", value: recap.reveals },
                  { label: "Best score", value: recap.best },
                  { label: "Badges", value: recap.badges },
                ].map((s) => (
                  <div key={s.label} className="bg-[#FAF8F5] rounded-2xl p-4 text-center">
                    <div className="font-heading text-3xl text-charcoal">{s.value}</div>
                    <div className="text-[10px] tracking-[0.12em] uppercase font-bold text-sage mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="bg-[#FAF8F5] rounded-2xl p-4 text-sm text-charcoal">
                Your health score is{" "}
                <span className="font-semibold" style={{ color: recap.delta >= 0 ? "var(--sage)" : "#C56B4A" }}>
                  {recap.delta > 0 ? `up ${recap.delta}` : recap.delta < 0 ? `down ${Math.abs(recap.delta)}` : "holding steady"}
                </span>{" "}
                since your first reveal — now at <span className="font-semibold">{recap.latest}/100</span>.
              </div>
              <div className="bg-[#FAF8F5] rounded-2xl p-4 text-sm text-charcoal">
                You've logged <span className="font-semibold">{recap.total_checkins}</span> goal check-in(s) with{" "}
                <span className="font-semibold">{recap.active_goals}</span> active goal(s). Keep it going! 💪
              </div>
              {user?.email ? (
                <button
                  data-testid="recap-email-btn"
                  onClick={sendRecap}
                  disabled={sendingRecap}
                  className="btn-primary w-full py-3.5 font-semibold flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" /> {sendingRecap ? "Sending…" : "Email this to me"}
                </button>
              ) : (
                <p className="text-xs text-muted-c text-center">Add your email in the reminders panel to email this to yourself.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, sub, positive }) => (
  <div className="bg-white rounded-3xl p-6 soft-shadow border border-black/5 flex flex-col justify-center">
    <div className="text-xs tracking-[0.15em] uppercase font-bold text-sage">{label}</div>
    <div className="font-heading text-4xl text-charcoal mt-1">{value}</div>
    <div className="text-xs mt-1" style={{ color: positive ? "var(--sage)" : "#C56B4A" }}>{sub}</div>
  </div>
);
